import Flag from '@components/Flag/Flag';
import FlagSelector, { countries } from '@components/FlagSelector/FlagSelector';
import Popover, { positionBounded } from '@components/Popover/Popover';
import Tooltip from '@components/Tooltip/Tooltip';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useNode from '@hooks/use-node';
import useSyncWhile from '@hooks/use-sync-while';
import { useComposedRefs } from '@reach/utils';
import type { CountryCallingCode, CountryCode } from 'libphonenumber-js';
import {
  getCountryCallingCode,
  getExampleNumber,
  isSupportedCountry,
  parsePhoneNumberFromString,
  parsePhoneNumberWithError,
} from 'libphonenumber-js';
import phoneNumberExamples from 'libphonenumber-js/examples.mobile.json';
import { useEffect, useMemo, useState } from 'react';
import { unresponsiveProperties } from 'ui-lib/src/lib/css/atoms/atomicProperties';

import styles from './PhoneNumberInput.module.scss';

export interface OnChangeDetails {
  /**
   * The localized version of the phone number with formatting
   * @example 0422 123 123
   */
  number: string;
  countryCode: CountryCode;
  countryCallingCode: CountryCallingCode;
  /**
   * The international version of the phone number with no formatting
   * @example +61422123123
   */
  internationalNumber: string;
  /**
   * The international version of the phone number with all formatting preserved
   * @example +61 422 123 123
   */
  internationalNumberFormatted: string;
  /**
   * The number as the user entered it with the country calling code attached
   * @example +610422 123 123
   */
  preservedNumber: string;
  isValid: boolean;
}

interface PhoneNumberInputProps {
  phoneNumber: string;
  countryCode?: CountryCode;
  errorMessage?: string;
  showError?: boolean;
  contained?: boolean;
  disabled?: boolean;
  required?: boolean;
  size?: 'small' | 'medium' | 'large';
  flagSelectorWidth?: 'strict' | 'min' | undefined;
  onChange: (details: OnChangeDetails) => void;
}

export function usePhoneNumberInput(
  initialNumber: string | undefined,
  initialCountry: string | undefined,
  hasBeenEdited: boolean,
): {
  countryCode: CountryCode | undefined;
  setCountryCode: React.Dispatch<React.SetStateAction<CountryCode | undefined>>;
  phoneNumber: string;
  setPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
} {
  const [countryCode, setCountryCode] = useState<CountryCode>();
  const [phoneNumber, setPhoneNumber] = useState('');

  // We had two options with how to structure the PhoneNumberInput props. We could have just accepted a phone number
  // that had the country code and number but this is awkward for the parent component that has to construct the number
  // even when it might not make sense (i.e. there is a country code but no number). The other option was to accept two
  // pieces of state, which makes more sense (I think) but we now need to do more work to display an existing number - we
  // need to split it apart into it's components.
  useSyncWhile(
    { initialNumber, initialCountry },
    ({ initialNumber, initialCountry }) => {
      if (initialNumber) {
        const { country, number } = splitPhoneNumberIntoParts(initialNumber);

        setPhoneNumber(number);

        if (country) {
          setCountryCode(country);
        } else if (initialCountry && isSupportedCountry(initialCountry)) {
          setCountryCode(initialCountry as CountryCode);
        }
      } else if (initialCountry && isSupportedCountry(initialCountry)) {
        setCountryCode(initialCountry as CountryCode);
      }
    },
    !hasBeenEdited,
  );

  return useMemo(
    () => ({
      countryCode,
      setCountryCode,
      phoneNumber,
      setPhoneNumber,
    }),
    [countryCode, setCountryCode, phoneNumber, setPhoneNumber],
  );
}

/**
 * Split a phone number into it's country code and number components while preserving all formatting
 * and extraneous information (e.g. +61 0422 123 123 has an unnecessary 0 at the beginning which this
 * will keep)
 * @param phoneNumber A phone number that can be internationalised or local
 */
export function splitPhoneNumberIntoParts(phoneNumber: string): {
  country: CountryCode | undefined;
  number: string;
} {
  // If the phone number is localised parsePhoneNumber will throw an error
  try {
    const parsedNumber = parsePhoneNumberWithError(phoneNumber);
    const country = parsedNumber.country;
    const number = phoneNumber.split(`+${parsedNumber.countryCallingCode}`)[1];
    return { country, number };
  } catch {
    // In the case of a localised number there is no country code we can extract
    return { country: undefined, number: phoneNumber };
  }
}

const PhoneNumberInput = ({
  ref: forwardedRef,
  phoneNumber = '',
  countryCode = 'US',
  flagSelectorWidth = 'min',
  onChange,
  disabled = false,
  required = false,
}: PhoneNumberInputProps & {
  ref?: React.Ref<HTMLInputElement>;
}) => {
  const [containerNode, containerRef] = useNode<HTMLDivElement>();
  const [showFlagSelector, setShowFlagSelector] = useState(false);
  const [popoverNode, popoverRef] = useNode<HTMLDivElement>();
  const [inputNode, inputRef] = useNode<HTMLInputElement>();
  const ref = useComposedRefs(inputRef, forwardedRef);
  const countryCallingCode = getCountryCallingCode(countryCode);
  const popoverZIndex = String(unresponsiveProperties.zIndex.overlay);

  const phoneNumberPlaceholder = useMemo(
    () => (countryCode ? getExampleNumber(countryCode, phoneNumberExamples)?.formatNational() : ''),
    [countryCode],
  );

  // Don't keep the flag selector open if the field becomes locked.
  useEffect(() => {
    if (disabled) {
      setShowFlagSelector(false);
    }
  }, [disabled]);

  function handleShowFlagSelector() {
    setShowFlagSelector((show) => !show);
  }

  function handleValueChange(newPhoneNumber: string) {
    const parsedPhoneNumber = parsePhoneNumberFromString(newPhoneNumber, countryCode);
    const formattedInternationalNumber = preservePhoneNumberFormatting({
      number: newPhoneNumber,
      internationalNumber: parsedPhoneNumber?.number?.toString() || '',
      countryCallingCode,
    });
    const preservedNumber = `+${countryCallingCode}${newPhoneNumber}`;

    onChange({
      number: newPhoneNumber,
      countryCode,
      countryCallingCode,
      internationalNumber: parsedPhoneNumber?.number?.toString() || '',
      internationalNumberFormatted: formattedInternationalNumber,
      preservedNumber,
      isValid: !!parsedPhoneNumber?.isValid(),
    });
  }

  function handleSelectCountryCode(countryCode: CountryCode) {
    const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber, countryCode);
    const countryCallingCode = getCountryCallingCode(countryCode);
    const formattedInternationalNumber = preservePhoneNumberFormatting({
      number: phoneNumber,
      internationalNumber: parsedPhoneNumber?.number?.toString() || '',
      countryCallingCode,
    });
    const preservedNumber = `+${countryCallingCode}${phoneNumber}`;

    onChange({
      number: phoneNumber,
      countryCode,
      countryCallingCode,
      internationalNumber: parsedPhoneNumber?.number?.toString() || '',
      internationalNumberFormatted: formattedInternationalNumber,
      preservedNumber,
      isValid: !!parsedPhoneNumber?.isValid(),
    });

    setShowFlagSelector(false);

    inputNode?.focus();
  }

  return (
    <>
      <div
        className={styles['phone-number-input']}
        ref={containerRef}
        data-disabled={disabled}
        style={{ '--country-calling-code-length': countryCallingCode.length }}
      >
        <div className={styles['input-container']}>
          <span className={styles['country-code']}>+{countryCallingCode}</span>

          <input
            type="tel"
            autoComplete="tel-national"
            value={phoneNumber}
            onChange={(event) => handleValueChange(event.target.value)}
            ref={ref}
            placeholder={phoneNumberPlaceholder}
            disabled={disabled}
            required={required}
          />
        </div>

        <Tooltip
          size="small"
          label={countries.find(({ code }) => code === countryCode)?.name || ''}
          aria-label=""
          show={!showFlagSelector}
        >
          <button
            className={styles['flag-selector-btn']}
            onClick={handleShowFlagSelector}
            type="button" // Otherwise this may submit whatever form it's embedded in
            data-active={showFlagSelector}
            disabled={disabled}
          >
            <span className={styles['selected-flag']}>
              <Flag countryCode={countryCode} />
            </span>
            <span className={styles['caret']}>
              <FontAwesomeIcon icon={faAngleDown} />
            </span>
          </button>
        </Tooltip>
      </div>

      <Popover
        show={showFlagSelector}
        onClose={(event) => {
          if (
            event.target instanceof HTMLElement &&
            event.target.className !== styles['flag-selector-btn']
          ) {
            setShowFlagSelector(false);
          }
        }}
        targetNode={containerNode}
        preventInteractionBubbling
        matchWidth={flagSelectorWidth}
        positioner={positionBounded({
          alignX: 'cover',
          xPreference: 'right',
          offsetY: 8,
          yPreference: 'bottom',
          adaptive: false,
        })}
        ref={popoverRef}
        style={{ zIndex: popoverZIndex }}
      >
        <FlagSelector
          show={showFlagSelector}
          containerNode={popoverNode}
          selectedCountryCode={countryCode}
          onSelect={handleSelectCountryCode}
        />
      </Popover>
    </>
  );
};

/**
 * Make a best effort attempt to preserve the formatting for a phone number whilst still having it follow
 * the I.164 format (i.e. international number format).
 * @example 0422 222 222 with +61 country calling code = +61 422 222 222
 * @example +610422 222 222 with +61 country calling code = +61 422 222 222
 * @example (201) 555-1023 with +1 country calling code = +1 (201) 555-1023
 * @example 422222 222 with +61 country calling code = +61 422222 222
 * @param details The user entered number, the international number and the country calling code
 */
function preservePhoneNumberFormatting(details: {
  number: string;
  countryCallingCode: CountryCallingCode;
  internationalNumber: string;
}) {
  let numbersDiverged = false;
  const reversedNumber = details.number.split('').reverse();
  let reversedNumberIndex = 0;

  let formattedInternationalNumber = '';

  // Loop through the characters in the international and user defined phone numbers from last to first
  // and insert any special formatting characters found in the user entered number as we go until the
  // two numbers diverge.
  for (const ch of details.internationalNumber.split('').reverse()) {
    // If the numbers no longer match that means that we have reached a point where the number that
    // the user entered diverges from the international version of the phone number. This is either because:
    // 1. The number that was entered by the user was the localised version (in which case some numbers
    // may have been removed for the international version)
    // 2. The number that was entered by the user was the international version, i.e. it included a
    // country calling code which was removed.
    // 3. We have reached the end of the number that was entered by the user
    //
    // In this case we just want to take the international version from here on out.
    if (numbersDiverged || !reversedNumber[reversedNumberIndex]) {
      formattedInternationalNumber = ch + formattedInternationalNumber;
    } else {
      // If we find any special characters used for formatting a number than insert those into the
      // final number
      while (reversedNumber[reversedNumberIndex]?.match(/[ ()\-,]/)) {
        formattedInternationalNumber =
          reversedNumber[reversedNumberIndex] + formattedInternationalNumber;
        reversedNumberIndex++;
      }

      // Record whether the numbers diverged
      numbersDiverged = reversedNumber[reversedNumberIndex] !== ch;

      formattedInternationalNumber = ch + formattedInternationalNumber;
      reversedNumberIndex++;
    }
  }

  // Add a space between the country calling code and the rest of the number
  formattedInternationalNumber = formattedInternationalNumber
    .split(new RegExp(`(\\+${details.countryCallingCode})`))
    .filter((part) => !!part)
    .join(' ');

  return formattedInternationalNumber;
}

PhoneNumberInput.displayName = 'PhoneNumberInput';

export default PhoneNumberInput;
